import { Portal, ViewFromProperty } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { ShellProviderProps } from '@aviv-estimation-funnel/shells/src/app/factory';
import { ResultStatus } from '@mfe-presentation/constants/userVerification';
import { IConfiguration, IStep } from '@mfe-types/form';
import type { City } from '@mfe-types/geo';
import { TLocale } from '@aviv-estimation-funnel/shells/src/app/helpers/i18n';

import { TLogo } from './logo';

export enum EActionType {
    SetCurrentStep = 'SET_CURRENT_STEP',
    SetStepValues = 'SET_STEP_VALUES',
    SetToastMessage = 'SET_TOAST_MESSAGE',
    SetUserVerification = 'SET_USER_VERIFICATION',
    SetEstimations = 'SET_ESTIMATIONS',
    SetSpecificTitle = 'SET_SPECIFIC_TITLE',
    SetCities = 'SET_CITIES',
    SetIsFromUpdatePage = 'SET_IS_FROM_UPDATE_PAGE',
    SetIsAuthenticated = 'SET_IS_AUTHENTICATED',
    SetHasSubmittedCurrentStep = 'SET_HAS_SUBMITTED_CURRENT_STEP',
    SetHasSubmittedPriceEstimateFeedback = 'SET_HAS_SUBMITTED_PRICE_ESTIMATE_FEEDBACK',
    SetHasUpdatedPhoneNumber = 'SET_HAS_UPDATED_PHONE_NUMBER',
}

export interface IUserVerification {
    isFetching: boolean;
    isVerificationRequired?: boolean;
    status?: ResultStatus;
    validationMethod?: string | null;
    verificationRequestId?: string;
    phoneNumber?: string;
}

export interface IEstimations {
    isFetching: boolean;
    id: string;
    createdAt: string;
    previousEstimationId: string;
    nextEstimationId: string;
    portal: string;
    platform: string;
    isAnUpdate: false;
    valuation: {
        rentMinPrice?: number;
        rentPrice?: number;
        rentMaxPrice?: number;
        rentSqmPrice?: number;
        rentPriceAccuracy?: number;
        rentRequestId?: string;
        sellMinPrice: number;
        sellPrice: number;
        sellMaxPrice: number;
        sellSqmPrice: number;
        sellPriceAccuracy: number;
        sellRequestId: string;
    };
    intent?: {
        isAPrivatePerson: boolean;
        isPropertyOwner: boolean;
        propertyMainUsage: string;
        ownerObjective: string;
        nonOwnerObjective: string;
        job: string;
        jobRole: string;
        hasAcceptedTermsAndConditions: true;
        authorContactDetailsId: string;
    };
    propertyCharacteristics?: {
        id: string;
        previousPropertyCharacteristicsId: string;
        nextPropertyCharacteristicsId: string;
        estateType: string;
        estateSubType: string;
        address: {
            cityName: string;
            houseNumber: [number, string];
            postalCode: string;
            streetName: string;
        };
        areas: {
            hasABalconyOrTerrace: boolean;
            hasABasement: boolean;
            hasACellar: boolean;
            hasAPrivateGarden: boolean;
        };
        conditions: {
            averageCondition: string;
            constructionYear: number;
            constructionPeriodMin: number;
            constructionPeriodMax: number;
            epcEnergyEfficiencyRating: string;
        };
        features: {
            hasAnElevator: true;
            hasAnOutdoorParking: false;
            hasAnIndoorParking: false;
            hasASwimmingPool: false;
            viewFromProperty: ViewFromProperty;
        };
        location: {
            addressId: string;
            buildingId: string;
            parcelId: string;
            streetId: string;
            blockId: string;
            irisId: string;
            boroughId: string;
            neighborhoodId: string;
            arrmunId: string;
            subcityId: string;
            cityId: string;
            districtId: string;
            subregionId: string;
            regionId: string;
            countryId: string;
            latitude: number;
            longitude: number;
        };
        legacyLocation: {
            addressId?: string;
            buildingId?: string;
            parcelId?: string;
            streetId?: string;
            blockId?: string;
            irisId?: string;
            boroughId?: string;
            neighborhoodId?: string;
            arrmunId?: string;
            subcityId?: string;
            cityId: string;
            districtId?: string;
            subregionId?: string;
            regionId?: string;
            countryId?: string;
            latitude: number;
            longitude: number;
        };
        numberOfAreas: {
            numberOfBathRooms: number;
            numberOfRooms?: number;
            numberOfBedRooms?: number;
        };
        numberOfFeatures: {
            numberOfParkingLots: number;
        };
        structure: {
            floorNumber: number;
            numberOfFloorsInBuilding: number;
        };
        surfaceAreas: {
            balconiesAndTerracesSurface: number;
            basementsSurface: number;
            livingSurface: number;
            terracesSurface: number;
            privateGardensSurface: number;
            totalSiteSurface: number;
        };
    };
    countEstimations?: number;
}

export interface ICurrentStep {
    displayedStepNumber?: number;
    name: string;
    index: number;
    customSubmit?: (
        fieldValues: Record<string, any>,
        config: IStep,
        name: string,
        locale: TLocale,
    ) => Promise<any>;
}

export interface IToastMessage {
    type?: 'error' | 'success' | 'warning';
    status: string;
    params?: Record<string, any>;
    persistent?: boolean;
}

export interface ISetCurrentStepAction {
    type: EActionType.SetCurrentStep;
    payload: ICurrentStep;
}

export interface ISetStepValuesAction {
    type: EActionType.SetStepValues;
    payload: Record<string, any>;
}

export interface ISetToastMessageAction {
    type: EActionType.SetToastMessage;
    payload: IToastMessage | undefined;
}

export interface ISetUserVerificationAction {
    type: EActionType.SetUserVerification;
    payload: IUserVerification;
}

export interface ISetEstimationsAction {
    type: EActionType.SetEstimations;
    payload: IEstimations;
}

export interface ISetSpecificTitleAction {
    type: EActionType.SetSpecificTitle;
    payload: string | undefined;
}

export interface ISetCitiesAction {
    type: EActionType.SetCities;
    payload: Array<City>;
}

export interface ISetIsFromUpdatePage {
    type: EActionType.SetIsFromUpdatePage;
    payload: boolean;
}

export interface ISetIsAuthenticated {
    type: EActionType.SetIsAuthenticated;
    payload: boolean;
}

export interface ISetHasSubmittedCurrentStep {
    type: EActionType.SetHasSubmittedCurrentStep;
    payload: boolean;
}

export interface ISetHasSubmittedPriceEstimateFeedback {
    type: EActionType.SetHasSubmittedPriceEstimateFeedback;
    payload: boolean;
}

export interface ISetHasUpdatedPhoneNumber {
    type: EActionType.SetHasUpdatedPhoneNumber;
    payload: boolean;
}

export interface IState {
    stepNames: Array<string>;
    maxStepIndex: number;
    maxDisplayedStep: number;
    formValues: Record<string, any>;
    currentStep: ICurrentStep;
    config: IConfiguration;
    cities: Array<City>;
    portal: Portal;
    logo: TLogo;
    useShellContext: () => ShellProviderProps | undefined;
    toastMessage?: IToastMessage | undefined;
    userVerification: IUserVerification;
    estimations: IEstimations;
    specificTitle?: string;
    isFromUpdatePage: boolean;
    isAuthenticated: boolean;
    hasSubmittedCurrentStep: boolean;
    hasSubmittedPriceEstimateFeedback: boolean;
    hasUpdatedPhoneNumber: boolean;
    timer: {
        seconds: number;
        startTimer: () => void;
        isTimerRunning: boolean;
        stopTimer: () => void;
    };
}

export type IAction =
    | ISetCurrentStepAction
    | ISetEstimationsAction
    | ISetStepValuesAction
    | ISetToastMessageAction
    | ISetUserVerificationAction
    | ISetSpecificTitleAction
    | ISetIsFromUpdatePage
    | ISetIsAuthenticated
    | ISetCitiesAction
    | ISetHasSubmittedCurrentStep
    | ISetHasSubmittedPriceEstimateFeedback
    | ISetHasUpdatedPhoneNumber;
